import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { articles, categories } from '@/mocks/data';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '0', 10);
  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该文章可能已被删除或不存在</p>
            <Link
              to="/"
              className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap no-underline"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: categoryInfo?.name || article.category, path: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <article className="w-full px-4 md:px-6 py-6">
          <div className="max-w-3xl mx-auto">
            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  {categoryInfo?.name || article.category}
                </span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 leading-snug mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <img
                    src={article.authorAvatar}
                    alt={article.author}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span className="text-sm font-medium text-foreground-700">{article.author}</span>
                </div>
                <span className="text-sm text-foreground-400">{article.date}</span>
                <span className="text-sm text-foreground-400">{article.readTime}阅读</span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed mb-10"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Action Bar */}
            <div className="flex items-center justify-between py-6 border-t border-b border-background-200/70 mb-10">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-primary-50 text-foreground-600 hover:text-primary-600 text-sm transition-colors cursor-pointer">
                  <i className="ri-heart-line"></i>
                  <span>{article.likes >= 1000 ? `${(article.likes / 1000).toFixed(1)}k` : article.likes}</span>
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-primary-50 text-foreground-600 hover:text-primary-600 text-sm transition-colors cursor-pointer">
                  <i className="ri-chat-1-line"></i>
                  <span>{article.comments}</span>
                </button>
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-primary-50 text-foreground-600 hover:text-primary-600 text-sm transition-colors cursor-pointer">
                  <i className="ri-share-forward-line"></i>
                  <span>分享</span>
                </button>
              </div>
              <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 hover:bg-primary-50 text-foreground-600 hover:text-primary-600 text-sm transition-colors cursor-pointer">
                <i className="ri-bookmark-line"></i>
                <span>收藏</span>
              </button>
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section>
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-5">相关推荐</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} {...ra} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>

      <Footer />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.image,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.date,
            publisher: {
              '@type': 'Organization',
              name: 'cy社区',
              url: 'https://cycommunity.com',
            },
            url: `https://cycommunity.com/article/${article.id}`,
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://cycommunity.com',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: categoryInfo?.name || article.category,
                item: `https://cycommunity.com/category/${article.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}